import { useMeta } from '../shared/useMeta';
import { ADSENSE_CLIENT, SITE_BRAND, SITE_NAME } from '../shared/site';
import '../content/seo.css';
import '../blog/blog.css';

/**
 * The privacy policy.
 *
 * Written from what the site actually does, not from a template: there is no
 * account system, no analytics and no cookie of our own, and the Firebase SDK
 * is tree-shaken out of the production bundle, so a boilerplate policy would
 * claim far more collection than really happens.
 *
 * The advertising section keys off `ADSENSE_CLIENT`, which is the same
 * constant that decides whether the ad script is served at all. Turning ads on
 * and describing them can therefore never drift apart.
 *
 * It borrows the blog's prose chrome but writes it out rather than importing
 * `ProseShell`, because that pulls react-router into the build-time
 * prerenderer, which bundles for Node and cannot resolve the router's dynamic
 * require of React. Plain anchors also cost nothing here: this is a page
 * people leave, not one they navigate around.
 */
export default function PrivacyPage() {
  const adsEnabled = ADSENSE_CLIENT !== '';

  useMeta({
    title: 'Privacy Policy',
    description:
      'What Puzzo stores, what it does not, and who else sees anything. No accounts, no analytics, and your puzzle progress never leaves your own browser.',
    path: '/privacy',
  });

  return (
    <div className="prose-page">
      <header className="prose-header">
        <a href="/" className="prose-home">
          ← {SITE_NAME}
        </a>
      </header>
      <article className="prose">
        <nav className="prose-crumbs" aria-label="Breadcrumb">
          <a href="/">All puzzles</a>
          <span aria-hidden> › </span>
          <span>Privacy</span>
        </nav>

        <h1>Privacy policy</h1>
        <p className="prose-meta">Last updated 30 September 2026</p>

        <p>
          {SITE_BRAND} is a free daily puzzle site. There is nothing to sign up for, and this
          policy describes everything the website does with data. It covers the website at
          everydaypuzzle.app. The iOS app is a separate product with its own policy in the App
          Store listing.
        </p>

        <h2>What we do not do</h2>
        <p>
          There are no accounts, so we never ask for your name, email address or any other
          identifying detail. We do not run analytics, we do not set a tracking cookie, and we do
          not build a profile of you or sell anything about you to anyone.
        </p>

        <h2>What your browser stores</h2>
        <p>
          The puzzles have to remember which ones you have played today, otherwise you could
          replay a daily and the result would mean nothing. That state lives in your own browser's
          local storage under keys that look like <code>daily:v1:cryptogram:2026-09-30</code>, and
          it holds the puzzle you are on, whether you won or lost, and your progress inside an
          unfinished round.
        </p>
        <p>
          It never leaves your device and we cannot read it. Clearing your browser's site data
          erases it, which also resets today's puzzles. Entries older than two weeks are deleted
          automatically.
        </p>

        <h2>What our servers see</h2>
        <p>
          The site is hosted on Cloudflare Pages. Like every web host, Cloudflare logs the
          requests it serves, including your IP address, the page you asked for and your browser's
          user-agent string. This is ordinary server logging used to serve the site and to defend
          it against abuse. We do not connect those logs to anything else, because there is
          nothing to connect them to.
        </p>
        <p>
          Each day's puzzles are fetched from our own endpoint at <code>/api/schedule</code>. The
          request carries no identifier of any kind — everyone is served the same puzzles, so
          there is nothing to personalise.
        </p>

        <h2>Other services the pages use</h2>
        <ul>
          <li>
            <strong>Google Fonts</strong> serves two typefaces. Your browser requests them from
            Google, which means Google sees the request and your IP address.
          </li>
          <li>
            <strong>Cloudflare</strong> hosts the site and serves every page, as described above.
          </li>
          <li>
            <strong>Apple</strong> receives nothing until you follow a link to the App Store, at
            which point you are on Apple's site under Apple's policy.
          </li>
        </ul>

        <h2>Advertising</h2>
        {adsEnabled ? (
          <>
            <p>
              We show ads supplied by Google AdSense. Google and its partners use cookies and
              similar technologies to serve and measure those ads, and in some regions to
              personalise them. Google's own description of what it collects is in{' '}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noreferrer noopener"
              >
                How Google uses information from sites that use its services
              </a>
              .
            </p>
            <p>
              If you are in the European Economic Area, the United Kingdom or Switzerland, you are
              asked for consent before any personalised advertising cookie is set, and you can
              change that choice at any time from the link in the consent banner. You can also opt
              out of personalised advertising across the web at{' '}
              <a
                href="https://adssettings.google.com"
                target="_blank"
                rel="noreferrer noopener"
              >
                Google Ads Settings
              </a>
              .
            </p>
          </>
        ) : (
          <p>
            The site does not currently show ads and no advertising code is loaded. If that
            changes, this section will describe exactly what the ad provider collects, and the
            change will go live at the same time as the ads rather than after them.
          </p>
        )}

        <h2>Children</h2>
        <p>
          The puzzles are suitable for all ages, but the site is not directed at children under 13
          and we do not knowingly collect anything from them. Since we collect no personal
          information from anyone, there is nothing held about a child to request the deletion of.
        </p>

        <h2>Your rights</h2>
        <p>
          Data protection law gives you rights to access, correct and delete personal data held
          about you. We hold none, so there is nothing to hand over or erase on request. The one
          thing connected to you is the state in your own browser, which you can clear yourself at
          any time through your browser's settings.
        </p>

        <h2>Changes</h2>
        <p>
          If this policy changes, the date at the top changes with it. Material changes, such as
          introducing advertising, will be reflected here before they take effect.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about this policy can go to{' '}
          {/* Cloudflare's Email Address Obfuscation rewrites anything that looks
              like an address into a JavaScript decoder, so the prerendered HTML
              said "[email protected]" to every crawler and to anyone without
              JS. These markers are Cloudflare's documented opt-out. */}
          <span
            dangerouslySetInnerHTML={{
              __html:
                '<!--email_off--><code>privacy@everydaypuzzle.app</code><!--/email_off-->',
            }}
          />
          .
        </p>
      </article>
    </div>
  );
}
